import { TableCell, TableRow } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { CSSProperties } from 'react'
import { DraggableProvided } from 'react-beautiful-dnd';
import { ColumnSpec } from './GenericTable';

interface Props<T> {
  /**
   * Specs for each of the columns of the table, including header names, cell builders, etc. Null values are provided as a declarative way of
   * including/excluding columns in a functional way - for instance, doing something like (`user.isEmployee() ? {header: "Admission date", content: ...} : null`)
   */
  columns: (ColumnSpec<T> | null)[];
  /**
   * Action to be executed when clicking on an element in the table
   */
  onElementClick?: (instance: T) => void;
  /**
   * Apply styles to the row according to some attribute of the instance
   */
  stylesForRowIfCondition?: (instance: T) => CSSProperties | undefined;
  /**
   * If `true`, the rows that has no Actions the menu won't be shown.
   */
  hideEmptyActionMenu?: boolean;

  index: number;
  row: T;
  draggable?: DraggableProvided;

  /**
   * If `true`, the row will be highlighted on hover
   */
  highlightOnHover?: boolean;
}

function GenericTableRow<T>(props: Props<T>) {
  const classes = useStyles();

  const RowBody = props.columns?.map((column, index) => {
    if (
      props.hideEmptyActionMenu &&
      column?.header === "Actions" &&
      column?.content(props.row).props.entries.every((menuEntry: any) => menuEntry === false)
    ) {
      return <TableCell key={index}></TableCell>;
    }

    return <TableCell key={index}>{column?.content(props.row)}</TableCell>;
  });

  return (
    <TableRow
      ref={props.draggable?.innerRef}
      {...props.draggable?.dragHandleProps}
      {...props.draggable?.draggableProps}
      key={props.index}
      // The pointer will only be shown if there is an element action or highlightOnHover is true
      className={(props.onElementClick || props.highlightOnHover) ? (props.highlightOnHover ? classes.pointerWithHighlight : classes.pointer) : ''}
      onClick={() => {
        if (props.onElementClick) props.onElementClick!(props.row);
      }}
      style={{ ...props.stylesForRowIfCondition && props.stylesForRowIfCondition(props.row), ...props.draggable?.draggableProps.style }}
    >
      {RowBody}
    </TableRow>
  )
}

const useStyles = makeStyles({
  pointerWithHighlight: {
    "&:hover": {
      backgroundColor: "#f5f5f5", //we should be able to pass the color as a prop.
    },
    cursor: "pointer",
  },
  table: {
    minWidth: 500,
  },
  tableHeader: {
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
  },
  pointer: {
    cursor: "pointer",
  },
  paginationContainer: {
    "& .MuiTablePagination-spacer": {
      flex: "none",
    },
  },
});

export default GenericTableRow;
